import React, { useEffect, useState } from 'react';
import Typography from '@primitives/Typography/Typography';
import { Button } from '@primitives';
import styles from '../index.module.scss';
import { WACallStates, WhatsappCallControls } from './interface';
import WhatsappShareGuide from './WhatsappShareGuide';

interface WhatsappCallStatusProps {
  wa: WhatsappCallControls;
}

const WhatsappCallStatus = (props: WhatsappCallStatusProps) => {
  const { wa } = props;
  const {
    state,
    grantTabShare,
    disconnect,
    resetCall,
    isCallActiveElsewhere,
    isSameCustomerCall,
    isOwnTabCallForDifferentCustomer,
    isCallForCurrentCustomer
  } = wa;
  const { status, error, recordingUrl, recordingMimeType } = state;

  const [showShareGuide, setShowShareGuide] = useState(false);
  const isViewerTabForSameCustomer = isSameCustomerCall;

  const handleProceedShare = () => {
    setShowShareGuide(false);
    grantTabShare();
  };

  useEffect(() => {
    if (
      status === WACallStates.NEEDS_TAB_SHARE &&
      !isCallActiveElsewhere &&
      !isViewerTabForSameCustomer
    ) {
      setShowShareGuide(true);
    }
    // Dismiss overlay when status moves past tab-share phase
    if (status !== WACallStates.NEEDS_TAB_SHARE && status !== WACallStates.IDLE) {
      setShowShareGuide(false);
    }
  }, [status, isCallActiveElsewhere, isViewerTabForSameCustomer]);

  const handleCancelShare = () => {
    setShowShareGuide(false);
    resetCall();
  };

  const isNumberNotFoundError =
    status === WACallStates.ERROR && /does not exist on WhatsApp/i.test(error ?? '');

  const showRecording =
    recordingUrl &&
    isCallForCurrentCustomer &&
    !isViewerTabForSameCustomer &&
    !isCallActiveElsewhere;

  const recordingExtension = recordingMimeType?.includes('mp4') ? '.m4a' : '.webm';

  return (
    <>
      {isOwnTabCallForDifferentCustomer && (
        <div className="mt-2 flex items-start gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
          <Typography variant="p5" className="text-amber-800">
            A WhatsApp call is active for another customer in this tab. Navigate back to manage the
            call.
          </Typography>
        </div>
      )}
      {isCallActiveElsewhere && (
        <div className="mt-2 flex items-start gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
          <Typography variant="p5" className="text-amber-800">
            A WhatsApp call is active in another tab. Please end that call to intiate a new call.
          </Typography>
        </div>
      )}
      {isCallForCurrentCustomer &&
        status !== WACallStates.IDLE &&
        !isOwnTabCallForDifferentCustomer && (
          <div className="mt-2 flex flex-col gap-1">
            {status === WACallStates.CHECKING_LOGIN && (
              <Typography variant="p5" className={styles.lightText}>
                Checking WhatsApp login status...
              </Typography>
            )}
            {status === WACallStates.NEEDS_TAB_SHARE &&
              !isViewerTabForSameCustomer &&
              !isCallActiveElsewhere && (
                <div className="flex flex-col gap-1">
                  <Typography variant="p5" className={styles.lightText}>
                    WhatsApp logged in. Select the WhatsApp tab and enable Share audio.
                  </Typography>
                </div>
              )}
            {status === WACallStates.SETTING_UP && (
              <Typography variant="p5" className={styles.lightText}>
                Setting up audio capture...
              </Typography>
            )}
            {status === WACallStates.IN_PROGRESS && (
              <Typography variant="p5" className={styles.lightText}>
                Calling via WhatsApp...
              </Typography>
            )}
            {status === WACallStates.CONNECTED && (
              <div className="flex flex-col gap-2">
                <Typography variant="p5">
                  <span className="successText">● WhatsApp call connected</span>
                </Typography>
                <Button
                  onClick={disconnect}
                  fullWidth
                  className="!bg-red-500 !text-white hover:!bg-red-600"
                >
                  Disconnect WhatsApp Call
                </Button>
              </div>
            )}
            {status === WACallStates.UPLOADING && (
              <Typography variant="p5" className={styles.lightText}>
                Uploading call recording...
              </Typography>
            )}
            {status === WACallStates.AWAITING_FEEDBACK && (
              <div className="flex items-start gap-2 mt-1 p-2 rounded-lg bg-green-50 border border-green-200">
                <Typography variant="p5" className="text-green-800">
                  Call ended. Fill feedback for Whatsapp Call.
                </Typography>
              </div>
            )}
            {isNumberNotFoundError && (
              <div className="flex items-start mt-1">
                <Typography variant="p5" className="text-[red]">
                  {error}
                </Typography>
              </div>
            )}
            {status === WACallStates.ERROR && !isNumberNotFoundError && (
              <Typography variant="p5">
                <span className="errorText">{error}</span>
              </Typography>
            )}
          </div>
        )}

      {showRecording && (
        <div className="flex flex-col gap-1 mt-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Typography variant="p5" className={styles.lightText}>
              You can download recording from here for filling feedback (if required).
            </Typography>
          </div>
          <audio controls src={recordingUrl!} className="w-full" style={{ height: 36 }} />
        </div>
      )}

      {showShareGuide &&
        isCallForCurrentCustomer &&
        !isCallActiveElsewhere &&
        !isViewerTabForSameCustomer &&
        !isOwnTabCallForDifferentCustomer && (
          <WhatsappShareGuide onCancel={handleCancelShare} onProceed={handleProceedShare} />
        )}
    </>
  );
};

export default React.memo(WhatsappCallStatus);
